import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useUserPreferences } from '~/redux/api/rolodex/useUserPreferences';

import { recordSidebarNavPreferenceMetric } from './recordSidebarNavPreferenceMetric';

interface SidebarNavPreferenceContextValue {
  isSidebarNavOnly: boolean;
  isOptedIn: boolean;
  isOptedInLoading: boolean;
  isOnboardingVisible: boolean;
  optIn: () => void;
  optOut: () => void;
  dismissOnboarding: () => void;
}

const SidebarNavPreferenceContext =
  createContext<SidebarNavPreferenceContextValue | null>(null);

export const SidebarNavPreferenceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { sidebarNavOptIn, setSidebarNavOptIn, isSidebarNavOptInLoading } =
    useUserPreferences();
  const isSidebarNavOnly = Boolean(useFlag(FlagType.SidebarNavOnly));
  const isOptedIn = isSidebarNavOnly || sidebarNavOptIn;
  const isOptedInLoading = !isSidebarNavOnly && isSidebarNavOptInLoading;

  const [isOnboardingVisible, setIsOnboardingVisible] = useState(false);

  const optIn = useCallback(() => {
    setSidebarNavOptIn(true);
    setIsOnboardingVisible(true);
    recordSidebarNavPreferenceMetric('opt_in');
  }, [setSidebarNavOptIn]);

  const optOut = useCallback(() => {
    setSidebarNavOptIn(false);
    recordSidebarNavPreferenceMetric('opt_out');
  }, [setSidebarNavOptIn]);

  const dismissOnboarding = useCallback(() => {
    setIsOnboardingVisible(false);
  }, []);

  const value = useMemo(
    () => ({
      isSidebarNavOnly,
      isOptedIn,
      isOptedInLoading,
      isOnboardingVisible,
      optIn,
      optOut,
      dismissOnboarding,
    }),
    [
      isSidebarNavOnly,
      isOptedIn,
      isOptedInLoading,
      isOnboardingVisible,
      optIn,
      optOut,
      dismissOnboarding,
    ],
  );

  return (
    <SidebarNavPreferenceContext.Provider value={value}>
      {children}
    </SidebarNavPreferenceContext.Provider>
  );
};

export const useSidebarNavPreference = () => {
  const context = useContext(SidebarNavPreferenceContext);
  if (!context) {
    throw new Error(
      'useSidebarNavPreference must be used within SidebarNavPreferenceProvider',
    );
  }
  return context;
};
